import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { listVaultEntries, isVaultAdmin as isVaultAdminFn, canAddVaultEntry, CATEGORY_LABEL, type VaultCategory } from "@/lib/vault";
import { AddVaultEntryModal } from "@/components/vault/AddVaultEntryModal";

export const Route = createFileRoute("/_app/vault")({ component: VaultPage });

const CATS: VaultCategory[] = [
  "paid_software", "ai_tools", "creative_assets", "reference_libraries", "accounts", "other_documents",
];

function VaultPage() {
  const { profile, role } = useAuth();
  const isAdmin = isVaultAdminFn(role);
  const canAdd = canAddVaultEntry(role, profile);
  const [addOpen, setAddOpen] = useState(false);

  const { data: entries = [] } = useQuery({
    queryKey: ["vault-entries"],
    queryFn: listVaultEntries,
    enabled: !!profile,
  });

  // Department users: limited overview
  if (!isAdmin) {
    const myOwned = entries.filter((e) => e.owner_id === profile?.id);
    const sharedWithMe = entries.filter((e) => e.owner_id !== profile?.id);
    const myRenewals = myOwned.filter((e) => {
      if (!e.renewal_date) return false;
      const diff = (new Date(e.renewal_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
      return diff >= 0 && diff <= 7;
    });
    const myCounts: Record<VaultCategory, number> = {
      paid_software: 0, ai_tools: 0, creative_assets: 0, reference_libraries: 0, accounts: 0, other_documents: 0,
    };
    for (const e of entries) myCounts[e.category]++;

    return (
      <div className="p-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Welcome, {profile?.full_name?.split(" ")[0] ?? "there"} 👋</h1>
            <p className="text-sm text-muted-foreground">Your tools, accounts, and shared resources</p>
          </div>
          <div className="flex gap-2">
            <Button asChild className="bg-foreground text-background hover:bg-foreground/90">
              <Link to="/vault/entries">Vault Entries →</Link>
            </Button>
            {canAdd && (
              <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => setAddOpen(true)}>
                + Add Entry
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-xl bg-foreground text-background p-5">
            <div className="text-[11px] uppercase opacity-70">My Entries</div>
            <div className="text-3xl font-bold mt-1">{myOwned.length}</div>
            <div className="text-xs opacity-70 mt-1">owned by me</div>
          </div>
          <KpiCard label="Shared With Me" value={sharedWithMe.length} sub="from team & departments" />
          <KpiCard label="Renewal Alerts" value={myRenewals.length} sub="this week" extra={myRenewals.length ? `${myRenewals.length} need attention` : "all good"} extraColor="text-violet-700" />
          <KpiCard label="Accessible Tools" value={entries.length} sub="total available" />
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-xs uppercase text-muted-foreground mb-3">Vault Categories</div>
          <div className="divide-y divide-border">
            {CATS.map((c) => (
              <div key={c} className="flex justify-between py-2 text-sm">
                <span>{CATEGORY_LABEL[c]}</span>
                <span className="text-muted-foreground">{myCounts[c]} entries</span>
              </div>
            ))}
          </div>
        </div>

        {myRenewals.length > 0 && (
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="text-xs uppercase text-muted-foreground mb-3">Your Renewals This Week</div>
            <div className="divide-y divide-border">
              {myRenewals.slice(0, 5).map((e) => (
                <div key={e.id} className="flex items-center justify-between py-2 text-sm">
                  <div>
                    <div className="font-medium">{e.name}</div>
                    <div className="text-xs text-muted-foreground">renews {e.renewal_date}</div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded bg-violet-100 text-violet-800">Renewal</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <AddVaultEntryModal open={addOpen} onOpenChange={setAddOpen} />
      </div>
    );
  }

  const total = entries.length;
  const sharedCount = entries.filter(
    (e) => (e.shared_users?.length ?? 0) + (e.shared_departments?.length ?? 0) > 0,
  ).length;
  const renewals = entries.filter((e) => {
    if (!e.renewal_date) return false;
    const d = new Date(e.renewal_date);
    const diff = (d.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 7;
  });
  const monthlySpend = entries.reduce((sum, e) => sum + Number(e.cost_amount ?? 0), 0);

  const counts: Record<VaultCategory, number> = {
    paid_software: 0, ai_tools: 0, creative_assets: 0, reference_libraries: 0, accounts: 0, other_documents: 0,
  };
  for (const e of entries) counts[e.category]++;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Good morning, {profile?.full_name?.split(" ")[0] ?? "Admin"} 👋</h1>
          <p className="text-sm text-muted-foreground">Secure access to tools, accounts, and important links</p>
        </div>
        <div className="flex gap-2">
          <Button asChild className="bg-foreground text-background hover:bg-foreground/90">
            <Link to="/vault/entries">Vault Entries →</Link>
          </Button>
          {canAdd && (
            <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => setAddOpen(true)}>
              + Add Entry
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="rounded-xl bg-foreground text-background p-5">
          <div className="text-[11px] uppercase opacity-70">Total Entries</div>
          <div className="text-3xl font-bold mt-1">{total}</div>
          <div className="text-xs opacity-70 mt-1">across all categories</div>
        </div>
        <KpiCard label="Shared With Team" value={sharedCount} sub="visible to members" extra="+12 from last week" />
        <KpiCard label="Renewal Alerts" value={renewals.length} sub="this week" extra={`${renewals.length} need attention`} extraColor="text-violet-700" />
        <KpiCard label="Monthly Tool Spend" value={`₹${monthlySpend.toLocaleString()}`} sub="subscriptions" extra="+₹3,800 from last month" extraColor="text-violet-700" />
        <KpiCard label="Password Views Today" value={12} sub="logged events" extra="+4 from yesterday" extraColor="text-purple-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-xs uppercase text-muted-foreground mb-3">Vault Categories</div>
          <div className="divide-y divide-border">
            {CATS.map((c) => (
              <div key={c} className="flex justify-between py-2 text-sm">
                <span>{CATEGORY_LABEL[c]}</span>
                <span className="text-muted-foreground">{counts[c]} entries</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-xs uppercase text-muted-foreground mb-3">Needs Attention</div>
          {renewals.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4">Nothing urgent.</div>
          ) : (
            <div className="divide-y divide-border">
              {renewals.slice(0, 5).map((e) => (
                <div key={e.id} className="flex items-center justify-between py-2 text-sm">
                  <div>
                    <div className="font-medium">{e.name}</div>
                    <div className="text-xs text-muted-foreground">renews {e.renewal_date}</div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded bg-violet-100 text-violet-800">Renewal</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-lg bg-blue-50 border border-blue-200 text-blue-900 text-sm px-4 py-3">
        As Admin/Moderator, you have full access to all vault entries. You can create, edit, share, transfer ownership, and access all options.
      </div>

      <AddVaultEntryModal open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}

function KpiCard({ label, value, sub, extra, extraColor }: { label: string; value: string | number; sub: string; extra?: string; extraColor?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="text-[11px] uppercase text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{sub}</div>
      {extra && <div className={`text-xs mt-1 ${extraColor ?? "text-muted-foreground"}`}>{extra}</div>}
    </div>
  );
}
